import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { BellRing, Send, Users, Mail, CalendarCheck, MapPin, Clock, MessageCircle, ExternalLink, Copy } from "lucide-react";
import { ROLE_LABEL } from "@/lib/constants";

export default function ReminderIbadah() {
  const { user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [channel, setChannel] = useState("EMAIL"); // EMAIL | WHATSAPP | WA_GROUP
  const [mode, setMode] = useState("ALL");
  const [selected, setSelected] = useState(new Set());
  const [customText, setCustomText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [waStatus, setWaStatus] = useState(null);
  const [waGroupPreview, setWaGroupPreview] = useState(null);
  const [form, setForm] = useState({
    subject: "Reminder H-7: Ibadah Persekutuan Perdana Klasis 30 September 2026",
    event_date: "Rabu, 30 September 2026",
    event_time: "Pukul 17.00 WIT",
    event_location: "GPDP Jemaat El Olam Timika",
    extra_note: "",
  });

  const canSend = ["SUPER_ADMIN","ADMIN_KLASIS","SEKRETARIS_KLASIS","KETUA_KLASIS"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const [c, l, w] = await Promise.all([
        api.get("/reminders/recipient-candidates"),
        api.get("/reminders/logs"),
        api.get("/reminders/whatsapp-status").catch(() => ({ data: null })),
      ]);
      setCandidates(c.data);
      setLogs(l.data);
      setWaStatus(w.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (canSend) load(); else setLoading(false); }, [canSend]);

  const toggleSelected = (uid) => {
    const next = new Set(selected);
    next.has(uid) ? next.delete(uid) : next.add(uid);
    setSelected(next);
  };

  const parsedCustom = useMemo(() => {
    return customText
      .split(/[\n,;]+/)
      .map((e) => e.trim())
      .filter((e) => e.length > 0);
  }, [customText]);

  const gembala = candidates.filter((c) => c.role === "GEMBALA_JEMAAT");
  const bpk = candidates.filter((c) => ["KETUA_KLASIS","WAKIL_KETUA","SEKRETARIS_KLASIS","WAKIL_SEKRETARIS","BENDAHARA_KLASIS","WAKIL_BENDAHARA","KOORDINATOR_BIDANG"].includes(c.role));
  const pengawas = candidates.filter((c) => ["BP2K","ADMIN_KLASIS"].includes(c.role));

  const openWaGroupLink = async () => {
    try {
      const { data } = await api.get("/reminders/whatsapp-link", {
        params: {
          event_date: form.event_date, event_time: form.event_time,
          event_location: form.event_location, extra_note: form.extra_note || "",
        },
      });
      setWaGroupPreview(data);
      // Open in new tab so admin can paste to group
      window.open(data.link, "_blank");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copyGroupText = async () => {
    if (waGroupPreview?.text) {
      await navigator.clipboard.writeText(waGroupPreview.text);
      toast.success("Teks disalin. Paste ke Group WhatsApp BPK Mimika.");
    }
  };

  const send = async () => {
    if (!canSend) return;
    if (mode === "SELECTED" && selected.size === 0) { toast.error("Pilih minimal 1 penerima"); return; }
    if (mode === "CUSTOM" && parsedCustom.length === 0) {
      toast.error(channel === "EMAIL" ? "Masukkan minimal 1 email" : "Masukkan minimal 1 nomor HP");
      return;
    }

    if (channel === "WHATSAPP" && !waStatus?.twilio_configured) {
      toast.error("Twilio belum dikonfigurasi. Isi TWILIO_ACCOUNT_SID & TWILIO_AUTH_TOKEN di backend/.env atau gunakan Group WhatsApp Link.");
      return;
    }

    const basePayload = {
      mode,
      event_date: form.event_date || undefined,
      event_time: form.event_time || undefined,
      event_location: form.event_location || undefined,
    };
    if (mode === "SELECTED") basePayload.user_ids = Array.from(selected);

    setSending(true); setResult(null);
    try {
      let data;
      if (channel === "EMAIL") {
        const payload = { ...basePayload, subject: form.subject || undefined };
        if (mode === "CUSTOM") payload.custom_emails = parsedCustom;
        ({ data } = await api.post("/reminders/ibadah-perdana", payload));
        toast.success(`${data.sent} email terkirim, ${data.failed} gagal.`);
      } else {
        const payload = { ...basePayload, extra_note: form.extra_note || undefined };
        if (mode === "CUSTOM") payload.custom_phones = parsedCustom;
        ({ data } = await api.post("/reminders/whatsapp", payload));
        toast.success(`${data.sent} WA terkirim, ${data.failed} gagal.`);
      }
      setResult(data);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  if (!canSend) {
    return (
      <AppShell>
        <div className="p-12 text-center border border-dashed border-border rounded-lg">
          <BellRing className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm text-muted-foreground">Hanya Sekretariat/Admin Klasis yang dapat mengirim reminder.</div>
        </div>
      </AppShell>
    );
  }

  const targetCount =
    mode === "ALL" ? candidates.filter(c => c.role !== "SEKRETARIS_JEMAAT" && c.role !== "BENDAHARA_JEMAAT").length :
    mode === "SELECTED" ? selected.size :
    parsedCustom.length;

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Sistem • Reminder</div>
        <h1 className="font-heading font-bold text-3xl mt-1 flex items-center gap-3">
          <BellRing className="h-7 w-7 text-secondary" /> Reminder Ibadah Persekutuan Perdana
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Kirim reminder H-7 ke pengurus BPK, BP2K, dan Gembala Jemaat via Email (Resend), WhatsApp individual (Twilio), atau Group WhatsApp BPK Mimika (link generator).
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Channel Selector */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Kanal Pengiriman</CardTitle></CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { key: "EMAIL", label: "Email", desc: "Resend via Emergent Email — undangan HTML resmi", icon: Mail },
                  { key: "WHATSAPP", label: "WhatsApp Individu", desc: waStatus?.twilio_configured ? "Twilio siap. Kirim WA ke tiap pengurus" : "Twilio belum dikonfigurasi (setup di .env)", icon: MessageCircle },
                  { key: "WA_GROUP", label: "Group WA (Link)", desc: "Generate link berisi teks reminder — paste manual ke Group BPK Mimika", icon: ExternalLink },
                ].map((opt) => {
                  const Ico = opt.icon;
                  const disabled = opt.key === "WHATSAPP" && !waStatus?.twilio_configured;
                  return (
                    <button
                      key={opt.key}
                      onClick={() => !disabled && setChannel(opt.key)}
                      disabled={disabled}
                      className={`text-left border rounded-md p-4 transition-all ${
                        channel === opt.key
                          ? "border-primary bg-primary/5 shadow-sm"
                          : disabled
                          ? "border-border opacity-50 cursor-not-allowed"
                          : "border-border hover:border-primary/50 hover:bg-muted/30"
                      }`}
                      data-testid={`btn-channel-${opt.key.toLowerCase()}`}
                    >
                      <div className={`flex items-center gap-2 font-semibold text-sm ${channel === opt.key ? "text-primary" : ""}`}>
                        <Ico className="h-4 w-4" /> {opt.label}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
              {!waStatus?.twilio_configured && (
                <div className="mt-3 text-[0.7rem] text-muted-foreground bg-amber-50 border border-amber-200 rounded-md p-2">
                  💡 Twilio WhatsApp belum aktif. Setup di <code className="bg-white px-1 rounded">backend/.env</code>: <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code>. Sementara gunakan opsi <strong>Group WA (Link)</strong> yang gratis.
                </div>
              )}
            </CardContent>
          </Card>

          {channel !== "WA_GROUP" && (
          /* Mode Selector - only for direct send channels */
          <Card>
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Mode Pengiriman</CardTitle></CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { key: "ALL", label: "Semua Pengurus", desc: "BPK, BP2K, dan seluruh Gembala Jemaat dari database aktif", icon: Users },
                  { key: "SELECTED", label: "Pilih Manual", desc: "Centang penerima secara spesifik", icon: Mail },
                  { key: "CUSTOM", label: channel === "EMAIL" ? "Email Custom" : "Nomor HP Custom", desc: channel === "EMAIL" ? "Broadcast ke daftar email khusus" : "Broadcast ke daftar nomor HP khusus (+62..)", icon: Send },
                ].map((opt) => {
                  const Ico = opt.icon;
                  return (
                    <button
                      key={opt.key}
                      onClick={() => setMode(opt.key)}
                      className={`text-left border rounded-md p-4 transition-all ${
                        mode === opt.key
                          ? "border-primary bg-primary/5 shadow-sm"
                          : "border-border hover:border-primary/50 hover:bg-muted/30"
                      }`}
                      data-testid={`btn-mode-${opt.key.toLowerCase()}`}
                    >
                      <div className={`flex items-center gap-2 font-semibold text-sm ${mode === opt.key ? "text-primary" : ""}`}>
                        <Ico className="h-4 w-4" /> {opt.label}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          )}

          {/* Recipients Detail - only when direct send */}
          {channel !== "WA_GROUP" && mode === "ALL" && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Daftar Penerima ({candidates.length})</CardTitle></CardHeader>
              <CardContent className="p-6 pt-0 text-xs space-y-3">
                {[{ label: "BPK Klasis", list: bpk }, { label: "BP2K & Admin", list: pengawas }, { label: "Gembala Jemaat", list: gembala }].map((grp) => (
                  <div key={grp.label}>
                    <div className="font-semibold text-foreground mb-1">{grp.label} ({grp.list.length})</div>
                    <div className="flex flex-wrap gap-1.5">
                      {grp.list.map((c) => <span key={c.id} className="bg-muted rounded-full px-2 py-0.5 text-[0.7rem]">{c.name}</span>)}
                    </div>
                  </div>
                ))}
                {channel === "WHATSAPP" && (
                  <div className="mt-3 text-[0.7rem] text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
                    ⚠️ WhatsApp memerlukan nomor HP di profil user. Kandidat tanpa nomor akan dilewati otomatis.
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {channel !== "WA_GROUP" && mode === "SELECTED" && (
            <Card>
              <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
                <CardTitle className="font-heading text-base">Pilih Penerima ({selected.size} dipilih)</CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={()=>setSelected(new Set(candidates.map(c=>c.id)))} data-testid="btn-select-all">Semua</Button>
                  <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={()=>setSelected(new Set())} data-testid="btn-select-none">Kosongkan</Button>
                </div>
              </CardHeader>
              <CardContent className="p-6 pt-0 max-h-96 overflow-y-auto">
                {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> :
                  <div className="space-y-1">
                    {candidates.map((c) => (
                      <label key={c.id} className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-muted/50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selected.has(c.id)}
                          onChange={()=>toggleSelected(c.id)}
                          data-testid={`checkbox-recipient-${c.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{c.name}</div>
                          <div className="text-[0.7rem] text-muted-foreground truncate">{c.email}{c.phone ? ` • 📱 ${c.phone}` : ""}</div>
                        </div>
                        <StatusBadge status="info" label={ROLE_LABEL[c.role] || c.role} />
                      </label>
                    ))}
                  </div>
                }
              </CardContent>
            </Card>
          )}

          {channel !== "WA_GROUP" && mode === "CUSTOM" && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="font-heading text-base">{channel === "EMAIL" ? `Daftar Email Custom (${parsedCustom.length})` : `Daftar Nomor HP Custom (${parsedCustom.length})`}</CardTitle></CardHeader>
              <CardContent className="p-6 pt-0 space-y-2">
                <Textarea
                  rows={6}
                  placeholder={channel === "EMAIL"
                    ? "tokohgereja@example.com&#10;tamu.undangan@example.com&#10;… (pisahkan dengan koma, titik-koma, atau baris baru)"
                    : "+6281234567890&#10;081298765432&#10;… (pisahkan dengan koma, titik-koma, atau baris baru)"}
                  value={customText}
                  onChange={(e)=>setCustomText(e.target.value)}
                  data-testid="input-custom-recipients"
                />
                {parsedCustom.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {parsedCustom.map((e, i) => <span key={i} className="bg-muted rounded-full px-2 py-0.5 text-[0.7rem]">{e}</span>)}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* WA Group Panel */}
          {channel === "WA_GROUP" && (
            <Card className="border-emerald-300 bg-emerald-50/40">
              <CardHeader className="pb-3"><CardTitle className="font-heading text-base flex items-center gap-2"><MessageCircle className="h-4 w-4 text-emerald-700" /> Kirim ke Group WhatsApp BPK Klasis Mimika</CardTitle></CardHeader>
              <CardContent className="p-6 pt-0 space-y-3">
                <div className="text-sm text-muted-foreground">
                  API WhatsApp resmi <strong>tidak mengizinkan kirim otomatis ke Group</strong>. Solusi: sistem generate teks reminder terformat + buka WhatsApp di device Anda → Anda pilih Group BPK dan paste. Total 2 klik.
                </div>
                <Button onClick={openWaGroupLink} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-open-wa-group">
                  <ExternalLink className="h-4 w-4 mr-2" /> Buka WhatsApp & Copy Teks
                </Button>
                {waGroupPreview && (
                  <div className="border border-border rounded-md p-3 bg-white text-xs">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold text-emerald-800">Teks Terkirim ke Chat Picker</div>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={copyGroupText} data-testid="btn-copy-wa-text"><Copy className="h-3 w-3 mr-1" /> Salin Teks</Button>
                    </div>
                    <pre className="whitespace-pre-wrap font-sans text-[0.75rem] leading-relaxed text-foreground">{waGroupPreview.text}</pre>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Message Preview */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Konten Undangan</CardTitle></CardHeader>
            <CardContent className="p-6 pt-0 space-y-3">
              {channel === "EMAIL" && (
                <div>
                  <Label className="text-xs">Subject Email</Label>
                  <Input value={form.subject} onChange={(e)=>setForm({...form, subject:e.target.value})} data-testid="input-reminder-subject" />
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div><Label className="text-xs flex items-center gap-1"><CalendarCheck className="h-3 w-3" /> Tanggal</Label><Input value={form.event_date} onChange={(e)=>setForm({...form, event_date:e.target.value})} data-testid="input-reminder-date" /></div>
                <div><Label className="text-xs flex items-center gap-1"><Clock className="h-3 w-3" /> Waktu</Label><Input value={form.event_time} onChange={(e)=>setForm({...form, event_time:e.target.value})} data-testid="input-reminder-time" /></div>
                <div><Label className="text-xs flex items-center gap-1"><MapPin className="h-3 w-3" /> Tempat</Label><Input value={form.event_location} onChange={(e)=>setForm({...form, event_location:e.target.value})} data-testid="input-reminder-location" /></div>
              </div>
              <div>
                <Label className="text-xs">Catatan Tambahan (opsional)</Label>
                <Textarea rows={2} value={form.extra_note} onChange={(e)=>setForm({...form, extra_note:e.target.value})} placeholder="Mohon konfirmasi kehadiran ke sekretariat…" data-testid="input-extra-note" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right sidebar */}
        <div className="space-y-4">
          {channel !== "WA_GROUP" ? (
            <Card className="border-primary/40 bg-primary/5">
              <CardContent className="p-6">
                <div className="text-[0.65rem] tracking-[0.18em] uppercase text-primary font-semibold">Siap Kirim</div>
                <div className="font-heading font-bold text-4xl mt-1 text-primary">{targetCount}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {channel === "EMAIL" ? "email siap terkirim" : "WhatsApp siap terkirim"}
                </div>
                <Button
                  className="w-full rounded-full mt-4"
                  onClick={send}
                  disabled={sending || targetCount === 0}
                  data-testid="btn-send-reminder"
                >
                  <Send className="h-4 w-4 mr-2" /> {sending ? "Mengirim…" : channel === "EMAIL" ? "Kirim Email" : "Kirim WhatsApp"}
                </Button>
                <div className="text-[0.65rem] text-muted-foreground mt-3 text-center">
                  {channel === "EMAIL" ? "Powered by Resend • Emergent Email" : "Powered by Twilio WhatsApp Business"}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-emerald-300 bg-emerald-50/40">
              <CardContent className="p-6">
                <div className="text-[0.65rem] tracking-[0.18em] uppercase text-emerald-800 font-semibold">Group WhatsApp</div>
                <div className="font-heading font-bold text-xl mt-1 text-emerald-900">BPK Klasis Mimika</div>
                <div className="text-xs text-muted-foreground mt-1">Klik tombol di kolom kiri untuk buka WhatsApp berisi teks reminder — Anda pilih Group BPK Mimika dan paste.</div>
                <div className="text-[0.65rem] text-muted-foreground mt-3 text-center">Powered by wa.me deep link (gratis, tanpa API)</div>
              </CardContent>
            </Card>
          )}

          {result && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-xs">Hasil Terakhir</CardTitle></CardHeader>
              <CardContent className="p-4 pt-0 text-xs">
                <div className="flex items-center justify-between border-b border-border pb-2 mb-2">
                  <span className="text-emerald-700 font-semibold">Terkirim: {result.sent}</span>
                  <span className={`font-semibold ${result.failed > 0 ? "text-rose-700" : "text-muted-foreground"}`}>Gagal: {result.failed}</span>
                </div>
                {result.errors?.length > 0 && (
                  <details className="text-[0.7rem] text-muted-foreground">
                    <summary className="cursor-pointer">Lihat error</summary>
                    <ul className="mt-1 space-y-0.5 list-disc list-inside">
                      {result.errors.slice(0, 5).map((e, i) => <li key={i}>{e.email || e.phone}: {e.error}</li>)}
                    </ul>
                  </details>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-xs">Riwayat Kirim</CardTitle></CardHeader>
            <CardContent className="p-4 pt-0 text-xs">
              {logs.length === 0 ? <div className="text-muted-foreground">Belum ada riwayat.</div> : (
                <ul className="space-y-2">
                  {logs.slice(0, 5).map((l) => (
                    <li key={l.id} className="border-l-2 border-border pl-2">
                      <div className="font-medium">{(l.type || "").includes("WA") ? "📱" : "✉️"} {l.mode || "ALL"} — {l.sent_count} terkirim</div>
                      <div className="text-muted-foreground">{new Date(l.triggered_at).toLocaleString("id-ID")} oleh {l.triggered_by}</div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
